import { Box, Button, Typography } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- standalone client-side history view, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { addMonths, format, parseISO } from 'date-fns';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar from '@sophocles/data-grids/components/toolbar';

function CicStatusGrid({ unitId, histories, fetchData, showFullList, openDetails }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const startDate = useMemo(() => {
    const v = searchParams.get('date_start');
    return v ? parseISO(v) : addMonths(new Date(), -2);
  }, [searchParams]);

  const endDate = useMemo(() => {
    const v = searchParams.get('date_end');
    return v ? parseISO(v) : new Date();
  }, [searchParams]);

  useEffect(() => {
    fetchData(unitId, format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'));
  }, [unitId, startDate, endDate, fetchData]);

  const handleDateRangeChange = useCallback(
    (start, end) => {
      const params = new URLSearchParams(location.search);
      params.set('date_start', format(start, 'yyyy-MM-dd'));
      params.set('date_end', format(end, 'yyyy-MM-dd'));
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    },
    [location, navigate],
  );

  const selectedStatusHistoryId = searchParams.get('id') || null;

  const rows = useMemo(
    () =>
      histories.toArray().map((entry, idx) => ({
        id: entry.get('id', idx),
        receive_time: entry.get('receive_time'),
        version: entry.get('version'),
        input_voltage: entry.get('input_voltage'),
        cpu_temperature: entry.get('cpu_temperature'),
        modem_temperature: entry.get('modem_temperature'),
      })),
    [histories],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'receive_time',
        headerName: t('relayController.receiveTime', { defaultValue: 'Receive Time' }),
        width: 200,
        valueFormatter: value => (value ? format(value, 'Pp') : ''),
      },
      { field: 'version', headerName: t('general.version', { defaultValue: 'Version' }), width: 100 },
    ];
    if (showFullList) {
      cols.push(
        {
          field: 'input_voltage',
          headerName: t('cicStatus.inputVoltageV', { defaultValue: 'Input Voltage (V)' }),
          width: 160,
          sortable: false,
        },
        {
          field: 'cpu_temperature',
          headerName: t('cicStatus.cpuTempC', { defaultValue: 'CPU Temp (°C)' }),
          width: 140,
          sortable: false,
          valueFormatter: value => (value == null ? '' : _.round(value, 3)),
        },
        {
          field: 'modem_temperature',
          headerName: t('cicStatus.modemTempC', { defaultValue: 'Modem Temp (°C)' }),
          width: 160,
          sortable: false,
          valueFormatter: value => (value == null ? '' : _.round(value, 3)),
        },
      );
    }
    cols.push({
      field: 'select',
      headerName: '',
      width: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Button size="small" variant="outlined" onClick={() => openDetails(params.row.id)}>
          {t('actions.select', { defaultValue: 'Select' })}
        </Button>
      ),
    });
    return cols;
  }, [t, showFullList, openDetails]);

  const toolbarContent = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TimeRangeSelector
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
          size="sm"
          exclude={['last1h', 'last24h']}
          defaultPreset="last30d"
        />
        {showFullList ? null : (
          <Typography variant="caption" color="text.secondary">
            {t('cicStatus.viewingDetails', { defaultValue: 'Viewing details' })}
          </Typography>
        )}
      </Box>
    ),
    [startDate, endDate, handleDateRangeChange, showFullList, t],
  );

  const toolbarActions = useMemo(
    () =>
      showFullList ? null : (
        <Button size="small" color="primary" onClick={() => openDetails(false)}>
          {t('cicStatus.showCurrentStatus', { defaultValue: 'Show Current Status' })}
        </Button>
      ),
    [showFullList, openDetails, t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <DataGridToolbar
        apiRef={apiRef}
        showQuickFilter
        toolbarContent={toolbarContent}
        toolbarActions={toolbarActions}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        hideFooter
        rowSelectionModel={{
          type: 'include',
          ids: new Set(selectedStatusHistoryId ? [Number(selectedStatusHistoryId)] : []),
        }}
        initialState={{ sorting: { sortModel: [{ field: 'receive_time', sort: 'desc' }] } }}
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default CicStatusGrid;
